<script setup lang="ts">
import { computed, nextTick, onScopeDispose, ref, watch } from '#imports';
import { useI18n } from 'vue-i18n';
import { useLocale } from '~/composables/useLocale';
import { useDialogFocus } from '~/composables/useDialogFocus';

const props = withDefaults(defineProps<{ menuItem?: boolean }>(), { menuItem: false });
const emit = defineEmits<{ changed: []; 'open-change': [open: boolean] }>();
const { t } = useI18n();
const { getCurrentLocale, setLocale, availableLocales } = useLocale();
const currentLocale = computed(() => getCurrentLocale());
const isOpen = ref(false);
const close = () => {
  isOpen.value = false;
  // If the desktop trigger disappeared at a breakpoint, return to mobile navigation.
  void nextTick(() => {
    if (!props.menuItem && window.innerWidth < 1024 && document.activeElement === document.body) {
      document.querySelector<HTMLButtonElement>('.mobile-header button')?.focus();
    }
  });
};
const { dialog, closeButton, handleKeydown } = useDialogFocus(isOpen, close);
watch(isOpen, open => emit('open-change', open));
// A nested account picker inherits its parent lock; standalone pickers own theirs.
const lockedAreas: Array<{ element: HTMLElement; overflow: string; inert: boolean }> = [];
const releaseAreas = () => {
  for (const { element, overflow, inert } of lockedAreas) {
    element.style.overflow = overflow;
    element.inert = inert;
  }
  lockedAreas.length = 0;
};
watch(isOpen, open => {
  if (props.menuItem || typeof document === 'undefined') return;
  releaseAreas();
  if (!open) return;
  for (const selector of ['[data-musicmint-page-scroll]', '.nav-container']) {
    const element = document.querySelector<HTMLElement>(selector);
    if (!element) continue;
    lockedAreas.push({ element, overflow: element.style.overflow, inert: element.inert });
    element.style.overflow = 'hidden';
    element.inert = true;
  }
}, { flush: 'post' });
onScopeDispose(releaseAreas);
const changeLanguage = (code: string) => {
  if (!setLocale(code)) return;
  close();
  emit('changed');
};
</script>

<template>
  <div class="language-switcher">
    <button type="button" aria-haspopup="dialog" :aria-expanded="isOpen" @click="isOpen = true"
      class="language-trigger" :class="{ 'menu-item-trigger': menuItem }">
      <span class="i-lucide-languages shrink-0" aria-hidden="true"></span>
      <span class="min-w-0 break-words">{{ t(menuItem ? 'settings.language' : 'common.language') }}</span>
      <span :class="menuItem ? 'i-lucide-chevron-right ml-auto' : 'i-lucide-chevron-down'" class="shrink-0" aria-hidden="true"></span>
    </button>
  </div>
  <Teleport to="body">
    <div v-if="isOpen" class="language-overlay" @click.self="close" @keydown="handleKeydown">
      <div ref="dialog" role="dialog" aria-modal="true" :aria-label="t('common.selectLanguage')" tabindex="-1" class="language-panel">
        <div class="language-heading">
          <h3 class="text-lg font-medium min-w-0 break-words">{{ t('common.selectLanguage') }}</h3>
          <button ref="closeButton" type="button" :aria-label="t('common.close')" @click="close" class="musicmint-dismiss">
            <span class="i-lucide-x h-4 w-4" aria-hidden="true"></span>
          </button>
        </div>
        <div class="language-options">
          <button v-for="locale in availableLocales" :key="locale.code" type="button"
            :aria-pressed="locale.code === currentLocale" @click="changeLanguage(locale.code)"
            class="language-option" :class="{ active: locale.code === currentLocale }">
            <span class="min-w-0 break-words">{{ locale.name }}</span>
            <span v-if="locale.code === currentLocale" class="i-lucide-check text-blue-400 shrink-0" aria-hidden="true"></span>
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.language-trigger {
  @apply flex min-h-[44px] items-center gap-2 px-3 py-2 rounded-md text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-700/30;
  @apply focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-300;
}
.menu-item-trigger {
  @apply w-full gap-3 px-4 py-3 rounded-none text-left hover:bg-gray-100 dark:hover:bg-gray-700;
}
.menu-item-trigger > .i-lucide-languages {
  @apply text-xl;
}
.language-overlay {
  @apply fixed inset-0 z-[250] flex items-center justify-center p-4 bg-black/60;
}
.language-panel {
  @apply flex flex-col w-full max-w-sm overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 shadow-xl;
  max-height: min(80dvh, calc(100dvh - 32px));
}
.language-heading {
  @apply flex shrink-0 items-center justify-between gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700;
}
.language-options {
  @apply min-h-0 overflow-y-auto overscroll-contain p-2;
}
.language-option {
  @apply flex w-full min-h-[44px] items-center justify-between gap-3 px-3 py-2 rounded-lg text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700;
  @apply focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-300;
}
.language-option.active {
  @apply bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300;
}
</style>
